import React from 'react';
import { ArrowLeft, X } from 'lucide-react';
import { IconButton } from '../../../components/ui';
import { NetworkAnalysis, pathsBetween, sharedConnections } from '../graphAnalytics';
import { networkPercentile } from './influenceInsights';
import { InfluenceEntity, InfluenceModel } from './influenceModel';
import { AnimatedBar, areaColor, EntityAvatar, PanelSection } from './ui';

export const MAX_COMPARE = 4;

interface ComparePanelProps {
  entities: InfluenceEntity[];
  model: InfluenceModel;
  analysis: NetworkAnalysis | null;
  onRemove: (id: string) => void;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
  onBack?: () => void;
  onClose: () => void;
  titleId?: string;
}

/** Side-by-side influence profiles, plus how the compared actors connect */
export const ComparePanel: React.FC<ComparePanelProps> = ({ entities, model, analysis, onRemove, onSelect, onHover, onBack, onClose, titleId }) => {
  const pairs: Array<{ a: InfluenceEntity; b: InfluenceEntity; hops: number | null; shared: InfluenceEntity[] }> = [];
  for (let i = 0; i < entities.length; i++) {
    for (let j = i + 1; j < entities.length; j++) {
      const a = entities[i];
      const b = entities[j];
      const path = analysis ? pathsBetween(analysis, a.id, b.id, 1)[0] : undefined;
      const shared = analysis
        ? sharedConnections(analysis, a.id, b.id)
            .map((id) => model.byId.get(id))
            .filter((e): e is InfluenceEntity => !!e)
        : [];
      pairs.push({ a, b, hops: path ? path.length - 1 : null, shared });
    }
  }
  const areas = model.areas.filter((area) => entities.some((e) => e.scoreByArea[area.id]));

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex flex-shrink-0 items-center gap-1 px-4 pb-2 pt-3">
        {onBack && (
          <IconButton label="Back to dossier" size="sm" onClick={onBack} className="-ml-1.5">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
        )}
        <h2 id={titleId} className="min-w-0 flex-1 truncate text-[15px] tracking-[-0.01em] text-ink">
          Compare <span className="tabular-nums text-neutral-400">{entities.length}/{MAX_COMPARE}</span>
        </h2>
        <IconButton label="End compare" size="sm" onClick={onClose} className="-mr-1.5">
          <X className="h-4 w-4" strokeWidth={1.75} />
        </IconButton>
      </header>

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain px-4 pb-6">
        <ul className="divide-y divide-neutral-200/70 rounded-xl border border-neutral-200">
          {entities.map((e, i) => (
            <li key={e.id} className="flex items-center gap-2.5 py-1.5 pl-3 pr-1.5" onMouseEnter={() => onHover(e.id)} onMouseLeave={() => onHover(null)}>
              <span className="w-3 text-[11px] tabular-nums text-neutral-400">{i + 1}</span>
              <EntityAvatar entity={e} size="xs" />
              <button type="button" onClick={() => onSelect(e.id)} className="min-w-0 flex-1 text-left">
                <span className="block truncate text-[13px] text-ink hover:underline">{e.label}</span>
                <span className="block truncate text-[11.5px] text-neutral-400">
                  avg {e.assessed ? e.average : '—'} · network {networkPercentile(analysis, e.id)}
                </span>
              </button>
              <IconButton label={`Remove ${e.label}`} size="xs" onClick={() => onRemove(e.id)}>
                <X className="h-3.5 w-3.5" strokeWidth={1.75} />
              </IconButton>
            </li>
          ))}
        </ul>
        {entities.length < 2 && <p className="text-[12.5px] text-neutral-500">Pick another entity and press Compare in its dossier.</p>}

        {entities.length > 0 && areas.length > 0 && (
          <PanelSection title="By area">
            <div className="space-y-4">
              {areas.map((area) => {
                const best = Math.max(...entities.map((e) => e.scoreByArea[area.id]?.score ?? 0));
                return (
                  <div key={area.id}>
                    <p className="mb-1.5 flex items-center gap-1.5 text-[12px] text-neutral-500">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: areaColor(area.color) }} />
                      {area.label}
                    </p>
                    <div className="space-y-1.5">
                      {entities.map((e, i) => {
                        const s = e.scoreByArea[area.id]?.score;
                        return (
                          <div key={e.id} className="flex items-center gap-2 text-[12px]">
                            <span className="w-3 text-[11px] tabular-nums text-neutral-400">{i + 1}</span>
                            <span className="w-24 flex-shrink-0 truncate text-neutral-600">{e.label}</span>
                            <AnimatedBar value={s ?? 0} color={area.color} className="flex-1" />
                            <span className={`w-6 text-right tabular-nums ${s != null && s === best && best > 0 ? 'text-ink' : 'text-neutral-400'}`}>{s ?? '—'}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </PanelSection>
        )}

        {pairs.length > 0 && (
          <PanelSection title="Between them">
            <ul className="space-y-2.5">
              {pairs.map(({ a, b, hops, shared }) => (
                <li key={`${a.id}-${b.id}`} className="rounded-xl bg-neutral-50 px-3 py-2">
                  <p className="truncate text-[12.5px] text-ink">
                    {a.label} <span className="text-neutral-400">·</span> {b.label}
                  </p>
                  <p className="mt-0.5 text-[12px] text-neutral-500">
                    {hops === null ? 'Not connected' : hops === 1 ? 'Directly tied' : `${hops} steps apart`}
                    {shared.length > 0 && ` · ${shared.length} shared ${shared.length === 1 ? 'tie' : 'ties'}`}
                  </p>
                  {shared.length > 0 && (
                    <p className="mt-1 flex flex-wrap gap-1">
                      {shared.slice(0, 4).map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => onSelect(s.id)}
                          className="rounded-md bg-white px-1.5 text-[11.5px] leading-5 text-neutral-600 ring-1 ring-inset ring-neutral-200 hover:text-ink"
                        >
                          {s.label}
                        </button>
                      ))}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </PanelSection>
        )}
      </div>
    </div>
  );
};
